import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Edit,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ViewTimeEntry = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [timeEntry, setTimeEntry] = useState(null);

  useEffect(() => {
    const loadTimeEntry = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.TIME_ENTRIES}/${id}`, {
          headers: API_CONFIG.HEADERS
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load time entry');
        }

        const data = await response.json();
        setTimeEntry(data.data?.timeEntry || data.data || data);
      } catch (err) {
        console.error('Error loading time entry:', err);
        showError(err.message || 'Failed to load time entry');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadTimeEntry();
    }
  }, [id, showError]);

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Time Entry Details"
          description="Loading time entry details..."
        />
        <div className="page-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!timeEntry) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Time Entry Details"
          description="Time entry not found"
        />
        <div className="page-container">
          <button
            type="button"
            className="btn btn-secondary d-flex align-items-center"
            onClick={() => navigate('/projects/time-tracking/entry')}
          >
            <ArrowLeft size={16} className="me-2" />
            Back to Time Entries
          </button>
        </div>
      </>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        backgroundColor: '#fef3c7', 
        color: '#92400e', 
        text: 'PENDING',
        icon: <AlertTriangle size={14} />
      },
      approved: { 
        backgroundColor: '#d1fae5', 
        color: '#065f46', 
        text: 'APPROVED',
        icon: <CheckCircle size={14} />
      },
      rejected: { 
        backgroundColor: '#fee2e2', 
        color: '#991b1b', 
        text: 'REJECTED',
        icon: <XCircle size={14} />
      }
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    
    return (
      <span className="badge d-flex align-items-center gap-1" style={{
        backgroundColor: config.backgroundColor,
        color: config.color,
        padding: '6px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        fontFamily: 'var(--font-family)',
        width: 'fit-content'
      }}>
        {config.icon}
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Time Entry Details"
        description="View complete time entry information"
        actions={[
          {
            label: 'Back',
            icon: <ArrowLeft size={18} />,
            onClick: () => navigate('/projects/time-tracking/entry'),
            variant: 'outline'
          },
          {
            label: 'Edit',
            icon: <Edit size={18} />,
            onClick: () => navigate(`/projects/time-tracking/entry/edit/${id}`),
            variant: 'primary'
          }
        ]}
      />
      <div className="page-container">
        <div className="project-create-container">
          <div className="project-form-card">
            {/* Basic Information */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Basic Information</h3>
              </div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Project</label>
                  <div className="form-control-static d-flex align-items-center gap-2">
                    <Target size={16} style={{ color: 'var(--primary-color)' }} />
                    <span>{timeEntry.project?.name || timeEntry.project || '—'}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Task</label>
                  <div className="form-control-static d-flex align-items-center gap-2">
                    <FileText size={16} style={{ color: 'var(--text-secondary)' }} />
                    <span>{timeEntry.task?.title || timeEntry.task || '—'}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <div className="form-control-static d-flex align-items-center gap-2">
                    <Calendar size={16} style={{ color: 'var(--text-secondary)' }} />
                    <span>{formatDate(timeEntry.date)}</span>
                  </div>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">Description</label>
                <div className="form-control-static">
                  <p style={{ margin: 0, color: 'var(--text-primary)' }}>
                    {timeEntry.description || '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Time Details */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Time Details</h3>
              </div>
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Start Time</label>
                  <div className="form-control-static d-flex align-items-center gap-2">
                    <Clock size={16} style={{ color: 'var(--text-secondary)' }} />
                    <span>{timeEntry.startTime || '—'}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">End Time</label>
                  <div className="form-control-static d-flex align-items-center gap-2">
                    <Clock size={16} style={{ color: 'var(--text-secondary)' }} />
                    <span>{timeEntry.endTime || '—'}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Duration</label>
                  <div className="form-control-static d-flex align-items-center gap-2">
                    <Clock size={16} style={{ color: 'var(--primary-color)' }} />
                    <span style={{ fontWeight: '600' }}>{timeEntry.duration || 0} hours</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <div className="form-control-static">
                    {getStatusBadge(timeEntry.status)}
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Information */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Billing Information</h3>
              </div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Billable</label>
                  <div className="form-control-static">
                    {timeEntry.billable ? (
                      <span className="badge" style={{
                        backgroundColor: '#d1fae5',
                        color: '#065f46',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>YES</span>
                    ) : (
                      <span className="badge" style={{
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>NO</span>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Hourly Rate</label>
                  <div className="form-control-static d-flex align-items-center gap-2">
                    <DollarSign size={16} style={{ color: 'var(--text-secondary)' }} />
                    <span>${(timeEntry.hourlyRate || 0).toFixed(2)}/hr</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Total Amount</label>
                  <div className="form-control-static d-flex align-items-center gap-2">
                    <DollarSign size={16} style={{ color: 'var(--success-color)' }} />
                    <span style={{ fontWeight: '600', fontSize: '16px' }}>
                      ${(timeEntry.totalAmount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            {(timeEntry.notes || timeEntry.approvedBy || timeEntry.approvedAt) && (
              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Additional Information</h3>
                </div>
                {timeEntry.notes && (
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <div className="form-control-static">
                      <p style={{ margin: 0, color: 'var(--text-primary)' }}>
                        {timeEntry.notes}
                      </p>
                    </div>
                  </div>
                )}
                {(timeEntry.approvedBy || timeEntry.approvedAt) && (
                  <div className="form-grid-2">
                    {timeEntry.approvedBy && (
                      <div className="form-group">
                        <label className="form-label">Approved By</label>
                        <div className="form-control-static d-flex align-items-center gap-2">
                          <User size={16} style={{ color: 'var(--text-secondary)' }} />
                          <span>{typeof timeEntry.approvedBy === 'object' 
                            ? (timeEntry.approvedBy.name || timeEntry.approvedBy.email || '—')
                            : timeEntry.approvedBy || '—'}</span>
                          </div>
                      </div>
                    )}
                    {timeEntry.approvedAt && (
                      <div className="form-group">
                        <label className="form-label">Approved At</label>
                        <div className="form-control-static d-flex align-items-center gap-2">
                          <Calendar size={16} style={{ color: 'var(--text-secondary)' }} />
                          <span>{formatDate(timeEntry.approvedAt)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px', 
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '1px solid var(--border-color)'
            }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/projects/time-tracking/entry')}
              >
                Back to List
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/projects/time-tracking/entry/edit/${id}`)}
              >
                <Edit size={16} className="me-2" />
                Edit Entry
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewTimeEntry;
